import React, { useState, useEffect } from 'react';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useNotificationSystem } from '../context/NotificationContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import './NotificationPrimer.css';

const SEEN_KEY = 'tunify.notifyPrimerSeen';
// Long enough for the app to finish its first paint, short enough to still read
// as part of opening the app rather than an interruption on top of it.
const DELAY_MS = 1200;

const seen = () => {
  try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return false; }
};
const markSeen = () => {
  try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode */ }
};

const isStandalone = () =>
  typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );

// ── FIRST-RUN NOTIFICATION PRIMER ────────────────────────────────────────────
// Asked once, the first time the INSTALLED app is opened.
//
// WHY A SCREEN OF OUR OWN, AND NOT JUST Notification.requestPermission().
// The browser's prompt can be answered "block" exactly once and then never
// appears again — the user has to dig through site settings to undo it, which
// nobody does. So the cheap dismissal ("Not now") is ours and costs nothing,
// and the browser is only asked once somebody has actually said yes. Asking at
// a moment when the reason is on screen also converts far better than a prompt
// fired at a blank page.
//
// ONLY IN THE INSTALLED APP. In a browser tab this would be a popup on a
// website; and on iPhone a tab cannot receive push at all, so the prompt would
// be a promise the platform refuses to keep.
export default function NotificationPrimer() {
  const { t } = useT();
  const { user } = useAuth();
  const { setBrowserPush } = useNotificationSystem();
  const push = usePushNotifications();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;              // nothing to notify about yet
    if (!isStandalone()) return undefined;         // browser tab — not our moment
    if (seen()) return undefined;
    if (typeof Notification === 'undefined') return undefined;
    // Already answered, either way. Re-asking cannot change a denial, and there
    // is nothing to ask someone who has already allowed it.
    if (Notification.permission !== 'default') return undefined;

    const id = setTimeout(() => setShow(true), DELAY_MS);
    return () => clearTimeout(id);
  }, [user?.uid]);

  const dismiss = () => { markSeen(); setShow(false); };

  const allow = async () => {
    markSeen();
    // Awaited BEFORE closing, so the permission request happens while this
    // click is still the active user gesture — browsers ignore one that isn't.
    await setBrowserPush(true);
    await push.enable();
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="npr" role="dialog" aria-modal="true" aria-labelledby="npr-title">
      <div className="npr-box">
        <div className="npr-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
            strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 01-3.46 0" />
          </svg>
        </div>

        <h2 className="npr-title" id="npr-title">{t('npr_title')}</h2>
        <p className="npr-text">{t('npr_text')}</p>

        <div className="npr-row">
          <button type="button" className="npr-btn" onClick={dismiss} disabled={push.busy}>
            {t('npr_later')}
          </button>
          <button type="button" className="npr-btn npr-btn--go" onClick={allow} disabled={push.busy}>
            {push.busy ? '…' : t('npr_enable')}
          </button>
        </div>
      </div>
    </div>
  );
}
